/**
 * The single entry point components in this app use to talk to Cognito.
 *
 * Wraps `react-oidc-context`'s `useAuth` and exposes an interface shaped like
 * the one the Auth0 SDK used to provide, so calling components stay small:
 * `isLoading` / `isAuthenticated` / `error` read the same, and `signIn`
 * replaces `loginWithRedirect`. Tokens for API calls come from
 * `auth/tokenProvider.js`, which never navigates.
 */
import { useCallback } from 'react';

import { useAuth as useOidcAuth } from 'react-oidc-context';

import { computeReturnTo, isSafeReturnTo } from '../lib/returnTo';
import { AuthMode, saveSignupAppContext } from '../lib/signupContext';
import { getMarketingParamsFromWindow } from '../utils/marketingParams';

export { AuthMode };

export const SKIP_PERMISSIONS_FLOW_KEY = 'pixelme:skip_permissions_flow';

/**
 * A full-page authorize redirect is a navigation, so two callers racing to
 * start one is not merely wasteful - the second `signinRedirect` overwrites
 * the PKCE verifier the first stored, and the eventual callback fails state
 * validation. Two places can trigger one here, the route guard and the
 * `/sign-in` page. Holding the promise at module scope, rather than in a component ref, dedupes
 * across all of them.
 */
let pendingAuthorizeRedirect = null;

/** Paths that must never become a post-login destination. */
const RETURN_TO_EXCLUDED_PATHS = ['/sign-in', '/sign-out', '/sign-up-c6', '/v2/sign-in', '/v2/sign-out'];

export function computeDefaultReturnTo() {
  return computeReturnTo(window.location.href, {
    excludePaths: RETURN_TO_EXCLUDED_PATHS,
  });
}

function buildSigninState(returnTo) {
  return { returnTo: isSafeReturnTo(returnTo) ? returnTo : undefined };
}

export function useCognitoAuth() {
  const oidcAuth = useOidcAuth();

  const startAuthorizeRedirect = useCallback(
    (args) => {
      if (oidcAuth.activeNavigator === 'signinRedirect') {
        return Promise.resolve();
      }

      if (pendingAuthorizeRedirect) {
        return pendingAuthorizeRedirect;
      }

      pendingAuthorizeRedirect = Promise.resolve(oidcAuth.signinRedirect(args)).finally(() => {
        pendingAuthorizeRedirect = null;
      });

      return pendingAuthorizeRedirect;
    },
    [oidcAuth],
  );

  /**
   * Starts Cognito Managed Login.
   *
   * Both sign-in and sign-up go here: Cognito's hosted pages handle
   * registration, and provisioning is enforced afterwards by the
   * `/api/v2/users/me` readiness gate rather than by a separate signup page.
   *
   * @param {object} [opts]
   * @param {'login'|'signup'} [opts.mode] Recorded as application context; it
   *   selects the post-callback handling, not a Cognito endpoint.
   * @param {string} [opts.emailHint] Prefills the Cognito form via `login_hint`.
   * @param {string} [opts.returnTo] Same-origin path to resume afterwards.
   * @param {boolean} [opts.forceLogin] Sends `prompt=login` to bypass hosted SSO.
   * @param {boolean} [opts.skipPermissionsFlow] PixelMe state for hub onboarding.
   */
  const signIn = useCallback(
    async (opts = {}) => {
      const mode = opts.mode || AuthMode.Login;
      const returnTo = opts.returnTo || computeDefaultReturnTo();
      const skipPermissionsFlow = !!opts.skipPermissionsFlow;

      // `skipPermissionsFlow` is PixelMe application state, never an IdP
      // authorization claim - it must not be sent to Cognito.
      try {
        window.sessionStorage.setItem(SKIP_PERMISSIONS_FLOW_KEY, String(skipPermissionsFlow));
      } catch {
        // Non-fatal: the flag only affects onboarding UX.
      }

      saveSignupAppContext({
        mode,
        skipPermissionsFlow,
        marketingParams: getMarketingParamsFromWindow(),
      });

      await startAuthorizeRedirect({
        state: buildSigninState(returnTo),
        ...(opts.emailHint ? { login_hint: opts.emailHint } : {}),
        ...(opts.forceLogin ? { prompt: 'login' } : {}),
      });
    },
    [startAuthorizeRedirect],
  );

  return {
    isLoading: oidcAuth.isLoading,
    isAuthenticated: oidcAuth.isAuthenticated,
    error: oidcAuth.error,
    user: oidcAuth.user,
    signIn,
  };
}
